import Phaser from "phaser";
import { FIGHTER, GAME, ATTACKS, ARCHETYPES, type AttackKind, type Archetype } from "../config";
import type { Controls } from "../input/Controls";
import { spriteKeyFor, packFor } from "./loader";

export type FighterSide = "left" | "right";

export type FighterState =
  | "idle" | "walk" | "jump" | "punch" | "kick"
  | "block" | "hit" | "knockdown" | "win" | "lose";

export interface FighterMetadata {
  name: string;
  slug: string;
  frameWidth: number;
  frameHeight: number;
  frames: number;
  animations: Record<string, { frames: number[]; frameRate: number; repeat: number }>;
}

export class Fighter {
  scene: Phaser.Scene;
  body: Phaser.Physics.Arcade.Sprite;
  side: FighterSide;
  controls: Controls;

  hp: number = FIGHTER.HP_MAX;
  hpMax: number = FIGHTER.HP_MAX;
  walkSpeed: number = FIGHTER.WALK_SPEED;
  jumpSpeed: number = FIGHTER.JUMP_SPEED;
  archetype: Archetype = "default";
  state: FighterState = "idle";
  meta: FighterMetadata;

  private nextPunchTime = 0;
  private nextKickTime  = 0;
  private hitStunUntil  = 0;
  private actionLockUntil = 0;

  nameLabel?: Phaser.GameObjects.Text;
  bandana?: Phaser.GameObjects.Rectangle;
  bandanaColor: number;
  name: string;
  slug: string;

  opponent?: Fighter;
  onAttack?: (attacker: Fighter, defender: Fighter, dmg: number, isBlocked: boolean, kind: AttackKind) => void;

  /** Текущая воспроизводящаяся анимация — чтобы не плодить заново. */
  private currentAnim: string = "";

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    side: FighterSide,
    controls: Controls,
    meta: FighterMetadata,
    bandanaColor: string | number = "#c41e3a",
    archetype: Archetype = "default",
  ) {
    this.scene = scene;
    this.side = side;
    this.controls = controls;
    this.meta = meta;
    this.name = meta.name;
    this.slug = meta.slug;
    this.bandanaColor = typeof bandanaColor === "string"
      ? parseInt(bandanaColor.replace("#",""), 16) : bandanaColor;
    this.archetype = archetype;
    const arch = ARCHETYPES[archetype] || ARCHETYPES.default;
    this.hpMax = arch.hp;
    this.hp = arch.hp;
    this.walkSpeed = arch.walk;
    this.jumpSpeed = arch.jump;

    // Регистрируем анимации для pack этого бойца (по slug → pack)
    this.registerAnimations();

    // Спрайт — pack текущего бойца (renegade/ranger/martial_hero)
    this.body = scene.physics.add.sprite(x, y, spriteKeyFor(meta.slug));
    this.body.setCollideWorldBounds(true);
    this.body.setOrigin(0.5, 1);
    this.body.setSize(FIGHTER.WIDTH * 0.7, FIGHTER.HEIGHT * 0.95);
    // Calibration offset (sprite внутри 128x192 имеет ноги внизу)
    this.body.setOffset(
      (meta.frameWidth - FIGHTER.WIDTH * 0.7) / 2,
      meta.frameHeight - FIGHTER.HEIGHT * 0.95,
    );

    // Спрайт boxer_* в исходнике смотрит влево, поэтому P1 (слева) нужно
    // зеркалить чтобы он смотрел вправо к P2.
    if (side === "left") {
      this.body.setFlipX(true);
    }

    // Спрайт уже физически окрашен под архетип — runtime-tint не нужен.
    this.body.clearTint();

    this.body.play(`anim:${packFor(meta.slug)}:idle`);
    this.currentAnim = "idle";
    // nameLabel и bandana убраны — имя бойца теперь только в HealthBar над аркой.
  }

  private registerAnimations() {
    const am = this.scene.anims;
    const pack = packFor(this.meta.slug);
    const sheetKey = `fighter:${pack}`;
    for (const [animName, def] of Object.entries(this.meta.animations)) {
      const key = `anim:${pack}:${animName}`;
      if (am.exists(key)) continue;
      am.create({
        key,
        frames: def.frames.map(f => ({ key: sheetKey, frame: f })),
        frameRate: def.frameRate,
        repeat: def.repeat,
      });
    }
  }

  setOpponent(opp: Fighter) { this.opponent = opp; }

  private playAnim(animName: string, ignoreIfPlaying = true) {
    const pack = packFor(this.meta.slug);
    const key = `anim:${pack}:${animName}`;
    if (ignoreIfPlaying && this.currentAnim === animName && this.body.anims.isPlaying) return;
    if (!this.scene.anims.exists(key)) return;
    this.body.play(key, true);
    this.currentAnim = animName;
  }

  update(time: number) {
    this.controls.update(time);

    if (this.hp <= 0) {
      this.state = "knockdown";
      this.body.setVelocityX(0);
      this.playAnim("knockdown");
      return;
    }

    if (time < this.hitStunUntil) {
      this.body.setVelocityX(0);
      this.state = "hit";
      this.playAnim("hit");
      return;
    }

    if (time < this.actionLockUntil) {
      this.body.setVelocityX(0);
      return;
    }

    if (this.controls.isBlock()) {
      this.state = "block";
      this.body.setVelocityX(0);
      this.playAnim("block");
      return;
    }

    // 1) если controls умеет говорить attackKind — берём напрямую
    const explicitKind = this.controls.attackKind?.() ?? null;
    if (explicitKind && time >= this.nextPunchTime && time >= this.nextKickTime) {
      this.attack(explicitKind, time);
      return;
    }

    // 2) fallback: punch+up = uppercut, обычный punch = jab.
    //    kick-кнопки на боксёре адаптированы под кулаки: LK→jab, HK→hp/uppercut.
    if (this.controls.isPunch() && time >= this.nextPunchTime) {
      const kind: AttackKind = this.controls.isUp() ? "uppercut" : "jab";
      this.attack(kind, time);
      return;
    }
    if (this.controls.isKick() && time >= this.nextKickTime) {
      // У нашего боксёра нет ног — переадресация на кулаки.
      const kind: AttackKind = this.controls.isUp() ? "uppercut" :
                               this.controls.isDown() ? "hp" : "hp";
      this.attack(kind, time);
      return;
    }

    if (this.controls.isUp() && this.body.body!.blocked.down) {
      this.body.setVelocityY(-this.jumpSpeed);
      this.state = "jump";
      if (this.meta.animations["jump"]) this.playAnim("jump", false);
    } else if (!this.body.body!.blocked.down && this.meta.animations["jump"]) {
      this.playAnim("jump", false);
    }

    let vx = 0;
    const speed = this.controls.isRun?.() ? this.walkSpeed * 1.6 : this.walkSpeed;
    if (this.controls.isLeft())  vx -= speed;
    if (this.controls.isRight()) vx += speed;
    this.body.setVelocityX(vx);

    if (this.body.body!.blocked.down) {
      if (vx !== 0) {
        this.state = "walk";
        this.playAnim("walk");
      } else {
        this.state = "idle";
        this.playAnim("idle");
      }
    }

    // Авто-разворот к оппоненту вне атаки. Спрайт по умолчанию смотрит влево,
    // поэтому: facing right → flipX=true; facing left → flipX=false.
    if (this.opponent && this.state !== "punch" && this.state !== "kick") {
      const facingRight = this.opponent.body.x > this.body.x;
      this.body.setFlipX(facingRight);
      this.side = facingRight ? "left" : "right";
    }
  }

  syncLabel() { /* no-op: label/bandana removed */ }

  private attack(kind: AttackKind, time: number) {
    const a = ATTACKS[kind];

    // Если в spritesheet есть отдельная анимация под этот kind — играем её,
    // иначе fallback на punch/kick категорию.
    const fallbackCat: "punch" | "kick" = (kind === "jab" || kind === "uppercut") ? "punch" : "kick";
    const animKey = this.meta.animations[kind] ? kind : fallbackCat;

    this.state = fallbackCat;
    this.body.setVelocityX(0);
    this.actionLockUntil = time + a.recovery * 0.6;
    this.playAnim(animKey, false);

    // tween-нагнётчик корпуса вперёд для импакта
    const dirSign = this.side === "left" ? 1 : -1;
    this.scene.tweens.add({
      targets: this.body,
      x: this.body.x + 14 * dirSign,
      duration: 80, yoyo: true, ease: "Quad.easeOut",
    });

    // recovery cooldown — общий для всех типов
    this.nextPunchTime = time + a.recovery;
    this.nextKickTime  = time + a.recovery;

    if (this.opponent) {
      // Forward-only: оппонент должен быть впереди по направлению лица.
      // facingSign = +1 если смотрим вправо, -1 если влево.
      const facingSign = this.side === "left" ? 1 : -1;
      const signedDx = (this.opponent.body.x - this.body.x) * facingSign;
      const dy = Math.abs(this.opponent.body.y - this.body.y);
      if (signedDx >= -10 && signedDx <= a.range && dy <= FIGHTER.HEIGHT) {
        const isBlocked = this.opponent.state === "block";
        const finalDmg = isBlocked
          ? Math.round(a.damage * FIGHTER.BLOCK_REDUCTION)
          : a.damage;
        this.opponent.takeHit(finalDmg, time, isBlocked, kind);
        this.onAttack?.(this, this.opponent, finalDmg, isBlocked, kind);
      }
    }
  }

  takeHit(damage: number, time: number, isBlocked: boolean, kind: AttackKind = "jab") {
    this.hp = Math.max(0, this.hp - damage);
    if (isBlocked) {
      // Лёгкий пуш на блоке
      const dir = this.opponent && this.opponent.body.x < this.body.x ? 1 : -1;
      this.body.setVelocityX(80 * dir);
      // Жёлтая вспышка блока — потом снимаем tint (спрайт сам цветной)
      this.body.setTint(0xffd700);
      this.scene.time.delayedCall(80, () => this.body.clearTint());
      return;
    }

    const a = ATTACKS[kind];
    this.hitStunUntil = time + (a.hitstun ?? FIGHTER.HIT_STUN);

    const dir = this.opponent && this.opponent.body.x < this.body.x ? 1 : -1;
    this.body.setVelocityX((a.vxOnHit ?? 200) * dir);
    if (a.knockup && a.vyOnHit) {
      this.body.setVelocityY(a.vyOnHit);
    }

    if (a.flash !== undefined) {
      this.body.setTint(a.flash);
      this.scene.time.delayedCall(120, () => this.body.clearTint());
    }
  }

  resetForNewRound(x: number) {
    this.hp = this.hpMax;
    this.state = "idle";
    this.hitStunUntil = 0;
    this.actionLockUntil = 0;
    this.nextPunchTime = 0;
    this.nextKickTime = 0;
    this.body.setPosition(x, GAME.GROUND_Y);
    this.body.setVelocity(0, 0);
    this.playAnim("idle");
  }
}
